// ============================================================================
// AI OPERATIONS ASSISTANT - Refactored using shared AIAssistantChat
// Chat-based interface for job insights, resource optimization, SLA monitoring
// Uses unified UI component for consistency across modules
// Date: January 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Calendar,
  Users,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Zap,
  ArrowRight,
  Clock,
  Timer,
  Wrench,
  BarChart3,
  Route,
  Factory,
  Recycle,
  Shield,
  Scale,
  UserCheck,
  Plus,
  Play,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  FileText,
  ToggleLeft,
  Hash,
  Type,
  List,
  Save,
  Eye,
  Layers,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAIAssistant } from '@/contexts/AIAssistantContext';
import { useModuleColor } from '@/hooks/useModuleColor';
import { useWorkItemOpener } from '@/hooks/useWorkItemOpener';

import { useOptionalOperations } from '@/contexts/OperationsContext';
import { normalizeSolutionItemTypes } from '@/lib/operations/itemTypes';
import { normalizeWorkflowTemplateDescriptors, type WorkflowTemplateDescriptor } from '@/lib/operations/solutionContext';
import AIAssistantChat from '@/components/shared/AIAssistantChat';
import type { AIMessage, AIAttachment, AISuggestedAction, QuickAction, ExamplePrompt } from '@/components/shared/AIAssistantChat';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface AIOperationsAssistantProps {
  permissions?: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
  contextJobId?: string;
  contextProjectId?: string;
  activeSolutionKey?: string | null;
  activeWorkflowTemplates?: WorkflowTemplateDescriptor[];
  initialPrompt?: string;
  contextWorkItem?: WorkItemAssistantContext | null;
  /** Candidate-scoped CV editing — mounts the assistant on the 'recruitment'
   *  context so it can return updated ParsedCv payloads (cv_preview). */
  contextCv?: CvAssistantContext | null;
  /** Called with the updated ParsedCv when the user applies a cv_preview. */
  onApplyCv?: (cv: Record<string, unknown>, opts?: { truncated?: boolean }) => void;
  /** Programmatic send — bump `key` to push `text` through the composer
   *  (e.g. profile-enrichment requests from the candidate designer). */
  autoSendRequest?: { key: string | number; text: string } | null;
  embedded?: boolean;
  floatingPanel?: boolean;
  onClose?: () => void;
  showProviderToggle?: boolean;
}

export interface WorkItemAssistantContext {
  workItemId: string;
  workItemNumber?: string | null;
  title: string;
  itemType: string;
  itemSubtype?: string | null;
  status?: string | null;
  priority?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  dueDate?: string | null;
  estimatedHours?: string | number | null;
  progressPercentage?: number | null;
  contactName?: string | null;
  locationName?: string | null;
  assigneeNames?: string[];
  activeTab?: string | null;
}

export interface CvAssistantContext {
  /** Candidate work-item id (uuid) — scopes the chat session. */
  candidateId?: string;
  candidateName?: string;
  currentTitle?: string;
  currentCompany?: string;
  email?: string;
  phone?: string;
  /** The live ParsedCv draft — sent as context.currentCv on every send. */
  currentCv?: Record<string, unknown> | null;
}

interface JobInsight {
  jobId?: string;
  jobNumber?: string;
  title: string;
  itemType?: string;
  permalinkUrl?: string | null;
  healthScore?: number;
  healthTrend?: 'improving' | 'stable' | 'declining';
  riskFactors?: string[];
  recommendations?: string[];
  slaStatus?: 'on_track' | 'at_risk' | 'breached';
  timeRemaining?: string;
  estimatedCompletion?: string;
}

interface ResourceAnalysis {
  technician: string;
  utilization: number;
  jobsToday: number;
  avgJobDuration: number;
  travelTime: number;
  availability: 'available' | 'busy' | 'overloaded';
  skills: string[];
}

interface OperationsKpis {
  completionRate?: number;
  slaCompliance?: number;
  avgResponseTime?: string | number;
  activeJobs?: number;
}

interface ScheduleSuggestionAssignment {
  jobTitle?: string;
  technician?: string;
  time?: string;
}

interface ScheduleSuggestion {
  confidence?: number;
  reasoning?: string;
  assignments?: ScheduleSuggestionAssignment[];
}

interface FormPreviewField {
  type?: string;
  label?: string;
  is_required?: boolean;
}

interface FormPreviewSection {
  title?: string;
  fields?: FormPreviewField[];
}

interface FormPreviewConfig {
  title?: string;
  description?: string;
  color?: string;
  category?: string;
  applies_to_item_type?: string;
  form_schema?: {
    sections?: FormPreviewSection[];
  };
}

interface AssistantProfile {
  title: string;
  subtitle: string;
  welcomeTitle: string;
  welcomeMessage: string;
  inputPlaceholder: string;
  quickActions: QuickAction[];
  examplePrompts: ExamplePrompt[];
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const BASE_EXAMPLE_PROMPTS: ExamplePrompt[] = [
  { icon: AlertTriangle, text: '🚨 What needs attention first across the active workflows today?', category: 'Priorities' },
  { icon: TrendingUp, text: '📈 Give me a concise summary of the active operational workload', category: 'Daily Summary' },
  { icon: Users, text: '👥 Where are the main workload bottlenecks right now?', category: 'Team Workload' },
  { icon: Clock, text: '⏰ Which items are overdue, blocked, or at risk?', category: 'Risk Review' },
];

const FORM_BUILDER_PROMPTS: ExamplePrompt[] = [
  { icon: ClipboardCheck, text: '📋 Create a practical checklist for the current workflow', category: 'Form Builder' },
  { icon: Wrench, text: '🧰 Build an inspection or completion form I can save', category: 'Form Builder' },
  { icon: FileText, text: '📝 Generate a structured operational report template', category: 'Form Builder' },
];

const BASE_QUICK_ACTIONS: QuickAction[] = [
  { id: 'priority_review', label: 'Needs Attention', icon: AlertTriangle, prompt: 'What needs attention first across the active workflows today?' },
  { id: 'team_workload', label: 'Team Workload', icon: Users, prompt: 'Summarise the current team workload and highlight bottlenecks or idle capacity.' },
  { id: 'today_summary', label: 'Today\'s Summary', icon: Calendar, prompt: 'Give me a concise summary of today across the active workflow templates.' },
  { id: 'overdue_items', label: 'Overdue Items', icon: Clock, prompt: 'Which items are overdue, blocked, or at risk right now?' },
  { id: 'workflow_forms', label: 'Create Form', icon: ClipboardCheck, prompt: 'Help me create an operational form or checklist for the current workflow templates.' },
];

const SOLUTION_SPECIFIC_PROMPTS: Record<string, ExamplePrompt[]> = {
  'legal-services': [
    { icon: Scale, text: '⚖️ Which matters need action in the next 7 days?', category: 'Matters' },
    { icon: FileText, text: '📑 What contracts are waiting for drafting, review, signatures, or client changes?', category: 'Contracts' },
    { icon: Calendar, text: '🗓️ Which court dates or hearings need preparation this week?', category: 'Court Diary' },
    { icon: Shield, text: '🛡️ Are any matters missing contact, contact person, or location relationships?', category: 'Risk & Data Quality' },
    { icon: BarChart3, text: '🏦 Show me trust-accounting or reconciliation exceptions that need review', category: 'Trust Accounting' },
  ],
  'waste-management-enterprise': [
    { icon: Recycle, text: '♻️ What\'s our diversion rate this week?', category: 'Waste Stats' },
    { icon: Route, text: '🚛 Any route inefficiencies today?', category: 'Route Check' },
    { icon: Shield, text: '🛡️ Check compliance status for permits', category: 'Compliance' },
  ],
  'field-services': [
    { icon: Wrench, text: '🔧 Which technicians have capacity?', category: 'Capacity' },
    { icon: Target, text: '📦 Are any jobs missing parts?', category: 'Inventory' },
    { icon: TrendingUp, text: '⭐ Customer satisfaction trends', category: 'CSAT' },
  ],
  'logistics-transport': [
    { icon: Route, text: '🚚 Delivery ETA status check', category: 'Deliveries' },
    { icon: AlertTriangle, text: '📍 Any delayed shipments?', category: 'Delays' },
    { icon: Route, text: '🗺️ Optimize delivery sequence', category: 'Optimization' },
  ],
  'advanced-manufacturing': [
    { icon: Factory, text: '🏭 Production bottlenecks today?', category: 'Production' },
    { icon: Target, text: '📋 Jobs waiting for materials', category: 'Materials' },
    { icon: Timer, text: '⏱️ Cycle time analysis', category: 'Efficiency' },
  ],
};

const SOLUTION_SPECIFIC_QUICK_ACTIONS: Record<string, QuickAction[]> = {
  'legal-services': [
    { id: 'matters_at_risk', label: 'Matters at Risk', icon: AlertTriangle, prompt: 'Which matters are at risk because of overdue tasks, missing court dates, or stalled next actions?' },
    { id: 'contract_queue', label: 'Contract Queue', icon: FileText, prompt: 'Summarise contracts that need drafting, review, client changes, signatures, or expiry follow-up.' },
    { id: 'court_diary', label: 'Court Diary', icon: Calendar, prompt: 'Show upcoming court dates, hearings, and diary items that need preparation.' },
    { id: 'conflict_watch', label: 'Conflict Watch', icon: Shield, prompt: 'Identify conflict-check risks or missing relationship data across our active legal matters.' },
    { id: 'trust_exceptions', label: 'Trust Exceptions', icon: BarChart3, prompt: 'Highlight trust accounting exceptions, unreconciled items, or matters that need finance follow-up.' },
  ],
};

function humanizeSolutionKey(solutionKey: string | null | undefined): string {
  return (solutionKey || 'operations')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function humanizeLabel(value: string | null | undefined): string {
  if (!value) return 'Unknown';
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatContextDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatTemplateSummary(workflowTemplates: WorkflowTemplateDescriptor[]): string {
  if (workflowTemplates.length === 0) {
    return '';
  }

  if (workflowTemplates.length <= 3) {
    return workflowTemplates.map((template) => template.templateName).join(' · ');
  }

  return `${workflowTemplates.slice(0, 3).map((template) => template.templateName).join(' · ')} +${workflowTemplates.length - 3} more`;
}

function buildAssistantProfile({
  solutionKey,
  solutionName,
  workflowTemplates,
}: {
  solutionKey: string | null;
  solutionName: string;
  workflowTemplates: WorkflowTemplateDescriptor[];
}): AssistantProfile {
  const templateSummary = formatTemplateSummary(workflowTemplates);
  const specificPrompts = solutionKey ? SOLUTION_SPECIFIC_PROMPTS[solutionKey] || [] : [];
  const specificQuickActions = solutionKey ? SOLUTION_SPECIFIC_QUICK_ACTIONS[solutionKey] : undefined;
  const resolvedSolutionName = solutionName || humanizeSolutionKey(solutionKey);
  const subtitle = templateSummary
    ? `Active workflows: ${templateSummary}`
    : 'Powered by Total Access';

  if (solutionKey === 'legal-services') {
    return {
      title: `AI Assistant - ${resolvedSolutionName}`,
      subtitle,
      welcomeTitle: 'How can I help with your legal workflow?',
      welcomeMessage: `I can help you triage matters, monitor contract review queues, prepare court diary follow-ups, surface trust-accounting exceptions, and flag conflict-check gaps.${templateSummary ? ` Active workflows: ${templateSummary}.` : ''}`,
      inputPlaceholder: 'Ask about matters, contracts, court dates, trust accounting, or conflicts...',
      quickActions: specificQuickActions || BASE_QUICK_ACTIONS,
      examplePrompts: [...specificPrompts.slice(0, 4), ...FORM_BUILDER_PROMPTS.slice(0, 1)],
    };
  }

  return {
    title: `AI Assistant - ${resolvedSolutionName}`,
    subtitle,
    welcomeTitle: `How can I help with ${resolvedSolutionName.toLowerCase()}?`,
    welcomeMessage: `I can help you analyse the active workload, highlight risks, guide next actions, and create forms or checklists using the active workflow templates.${templateSummary ? ` Active workflows: ${templateSummary}.` : ''}`,
    inputPlaceholder: 'Ask about workload, risks, assignments, or describe a form to create...',
    quickActions: specificQuickActions || BASE_QUICK_ACTIONS,
    examplePrompts: [
      ...BASE_EXAMPLE_PROMPTS.slice(0, 3),
      ...FORM_BUILDER_PROMPTS.slice(0, 1),
      ...specificPrompts.slice(0, 2),
    ],
  };
}

function buildWorkItemAssistantProfile(contextWorkItem: WorkItemAssistantContext): AssistantProfile {
  const identifier = contextWorkItem.workItemNumber || contextWorkItem.workItemId;
  const itemTypeLabel = humanizeLabel(contextWorkItem.itemType);
  const activeTabLabel = contextWorkItem.activeTab ? humanizeLabel(contextWorkItem.activeTab) : null;
  const scheduledSummary = formatContextDate(contextWorkItem.scheduledStart);
  const dueSummary = formatContextDate(contextWorkItem.dueDate);
  const assigneeLabel = Array.isArray(contextWorkItem.assigneeNames) && contextWorkItem.assigneeNames.length > 0
    ? contextWorkItem.assigneeNames.join(', ')
    : 'unassigned';
  const isJobContext = contextWorkItem.itemType === 'job';
  const isAppointmentContext = contextWorkItem.itemType === 'appointment';
  const isProjectContext = contextWorkItem.itemType === 'project';
  const isTeamMemberContext = contextWorkItem.itemType === 'team_member';
  const schedulingModelMessage = isJobContext
    ? 'Jobs are not scheduled directly — linked appointments or linked appointment groups carry the schedule.'
    : isAppointmentContext
      ? 'Appointments are the actual schedule records for the parent job, so think of this as one linked appointment unless the plan groups multiple appointments together.'
      : isProjectContext
        ? 'Projects coordinate jobs, and each job is then scheduled through linked appointments or linked appointment groups.'
        : isTeamMemberContext
          ? 'This is a team member profile, so review the live profile, schedule, licenses, capacity, Smart Task state, and vehicle-inspection history together.'
        : null;

  const basePrompt = isJobContext
    ? `Review the current appointment plan for ${identifier}. Jobs are scheduled through linked appointments and linked appointment groups, so recommend the next appointment-led action, sequencing change, and customer or technician follow-up.`
    : isProjectContext
      ? `Review the jobs inside ${identifier} and tell me which ones need the next linked appointment or linked appointment group planned first.`
    : `Help me review the scheduling, sequencing, and next best actions for ${identifier}.`;
  const appointmentsPrompt = isJobContext
    ? `Review all linked appointments currently tied to ${identifier} and recommend the best sequencing, technician fit, customer communication, and next slot.`
    : isAppointmentContext
      ? `Review this linked appointment for ${identifier} and recommend the best preparation, travel, timing, and follow-up actions.`
      : `Review the current appointment plan for ${identifier} and recommend the best sequencing, technician fit, customer communication, and next slot.`;
  const timePrompt = isJobContext
    ? `Review the logged time and appointment cadence for ${identifier}, then tell me whether another linked appointment or linked appointment group is needed next.`
    : isAppointmentContext
      ? `Review the logged time and schedule drift for this linked appointment, then tell me what should change next for ${identifier}.`
      : `Review the logged time and schedule drift for ${identifier}, then tell me what should change next.`;
  const breakdownPrompt = isJobContext
    ? `Turn the current work breakdown for ${identifier} into a practical appointment-led execution plan, including whether the work should be handled as one linked appointment or a linked appointment group.`
    : isProjectContext
      ? `Turn the jobs inside ${identifier} into a practical rollout plan, including which jobs need linked appointments, which need linked appointment groups, and what should happen first.`
    : `Turn the current work breakdown for ${identifier} into a practical execution sequence and appointment plan.`;
  const appointmentPrompt = `Review this linked appointment and tell me the next best preparation, travel, scheduling, and follow-up actions for ${identifier}.`;
  const nextBestSlotPrompt = isJobContext
    ? `Recommend the next linked appointment or linked appointment group needed to move ${identifier} forward, including timing and owner suggestions.`
    : isAppointmentContext
      ? `Suggest the best follow-up move for this linked appointment, including whether another linked appointment is needed after ${identifier}.`
      : isProjectContext
        ? `Recommend which job inside ${identifier} needs the next linked appointment or linked appointment group planned first.`
        : `Suggest the next best appointment slot and resource plan for ${identifier}.`;
  const executionPlanPrompt = isJobContext
    ? `Create a concise appointment-led execution and follow-up plan for ${identifier} based on the current job, appointment, and work-breakdown context.`
    : isAppointmentContext
      ? `Create a concise execution and follow-up plan for this APT based on the current appointment context for ${identifier}.`
    : `Create a concise execution and follow-up plan for ${identifier} based on the current work-item context.`;

  const activePrompt = contextWorkItem.itemType === 'appointment'
    ? appointmentPrompt
    : isTeamMemberContext && contextWorkItem.activeTab === 'team_schedule'
      ? 'Review this team member’s schedule, sequencing, and travel context, and call out any conflicts or gaps.'
      : isTeamMemberContext && contextWorkItem.activeTab === 'team_capacity'
        ? 'Review this team member’s capacity, workload, budget, and utilisation, then highlight bottlenecks or spare capacity.'
        : isTeamMemberContext && contextWorkItem.activeTab === 'team_licenses'
          ? 'Review this team member’s licenses and compliance status, and call out anything that could affect fleet or job assignments.'
          : isTeamMemberContext && (contextWorkItem.activeTab === 'team_smart_task' || contextWorkItem.activeTab === 'team_vehicle_inspections')
            ? 'Review this team member’s live SmartTask state and vehicle-inspection history, then summarise the linked vehicle and any blockers.'
            : isTeamMemberContext
              ? 'Review this team member profile and summarise schedule, license, capacity, SmartTask, and vehicle-inspection issues that need attention.'
    : contextWorkItem.activeTab === 'appointments'
      ? appointmentsPrompt
      : contextWorkItem.activeTab === 'time'
        ? timePrompt
        : contextWorkItem.activeTab === 'work_breakdown' || contextWorkItem.activeTab === 'subtasks'
          ? breakdownPrompt
          : basePrompt;

  return {
    title: isTeamMemberContext ? 'AI Team Member Assistant' : 'AI Scheduling Assistant',
    subtitle: [
      isJobContext ? 'Appointment planning' : isAppointmentContext ? 'Appointment review' : isProjectContext ? 'Job scheduling' : isTeamMemberContext ? 'People operations' : 'Gemini context',
      identifier,
      itemTypeLabel,
      activeTabLabel ? `${activeTabLabel} tab` : null,
    ].filter(Boolean).join(' • '),
    welcomeTitle: isJobContext
      ? `Plan Appointments for ${identifier}`
      : isAppointmentContext
        ? `Review Appointment ${identifier}`
        : isProjectContext
          ? `Plan Job Schedules in ${identifier}`
          : isTeamMemberContext
            ? `Review Team Member ${identifier}`
          : `Plan ${identifier}`,
    welcomeMessage: isTeamMemberContext
      ? `I’m focused on ${identifier} — ${contextWorkItem.title}. ${schedulingModelMessage ? `${schedulingModelMessage} ` : ''}I can help with schedule gaps, smart task state, vehicle assignments, licenses, capacity, compliance, and vehicle-inspection follow-up.${scheduledSummary ? ` Scheduled for ${scheduledSummary}.` : ''}${dueSummary ? ` Due ${dueSummary}.` : ''} Assignees: ${assigneeLabel}.`
      : `I’m focused on ${identifier} — ${contextWorkItem.title}. ${schedulingModelMessage ? `${schedulingModelMessage} ` : ''}I can help with scheduling, sequencing, workload risks, technician/customer communication, and follow-up actions.${scheduledSummary ? ` Scheduled for ${scheduledSummary}.` : ''}${dueSummary ? ` Due ${dueSummary}.` : ''} Assignees: ${assigneeLabel}.`,
    inputPlaceholder: isJobContext
      ? 'Ask how this job should be turned into the next linked appointment or linked appointment group, what to resequence, or what to tell the customer...'
      : isAppointmentContext
        ? 'Ask what should change on this appointment, whether a follow-up appointment is needed, or what to tell the customer...'
        : isProjectContext
          ? 'Ask which jobs need linked appointments or linked appointment groups next, what to sequence first, or where the rollout is drifting...'
          : isTeamMemberContext
            ? 'Ask about the team member’s schedule, licenses, capacity, SmartTask state, or linked vehicle and inspections...'
          : 'Ask for the next best slot, risk review, sequencing, or a customer-ready update...',
    quickActions: [
      { id: 'review_schedule', label: isJobContext ? 'Review Appointment Plan' : isAppointmentContext ? 'Review Appointment' : isProjectContext ? 'Review Job Schedule' : 'Review Schedule', icon: Calendar, prompt: activePrompt },
      { id: 'next_best_slot', label: isJobContext ? 'Next Appointment Step' : isAppointmentContext ? 'Next Appointment Step' : isProjectContext ? 'Next Job Appointment Step' : 'Next Best Slot', icon: Clock, prompt: nextBestSlotPrompt },
      { id: 'capacity_check', label: isJobContext ? 'Appointment Capacity' : isAppointmentContext ? 'Appointment Capacity' : isProjectContext ? 'Job Capacity' : isTeamMemberContext ? 'People Capacity' : 'Capacity Check', icon: Users, prompt: isJobContext ? `Check whether the current appointment plan, assignees, and timing for ${identifier} create workload or sequencing conflicts.` : isAppointmentContext ? `Check whether this appointment has the right assignee, timing, and travel setup for ${identifier}.` : isProjectContext ? `Check which jobs inside ${identifier} are missing realistic linked appointment or linked appointment group capacity planning.` : isTeamMemberContext ? `Check whether ${identifier} has too much or too little work, and whether the vehicle, schedule, or compliance context needs attention.` : `Check whether the current assignees and schedule for ${identifier} create workload or timing conflicts.` },
      { id: 'customer_update', label: isJobContext ? 'Appointment Update' : isAppointmentContext ? 'Appointment Update' : 'Customer Update', icon: MessageSquare, prompt: isProjectContext ? `Draft a short customer-ready update covering the next jobs, linked appointments, or linked appointment groups planned for ${identifier}.` : `Draft a short customer-ready scheduling update for ${identifier}.` },
      { id: 'execution_plan', label: isJobContext ? 'Appointment Plan' : isAppointmentContext ? 'Appointment Plan' : isProjectContext ? 'Rollout Plan' : 'Plan Execution', icon: ClipboardCheck, prompt: executionPlanPrompt },
    ],
    examplePrompts: [
      { icon: Calendar, text: appointmentsPrompt, category: isJobContext ? 'Appointment Plan' : isAppointmentContext ? 'Appointment Review' : isProjectContext ? 'Job Scheduling' : isTeamMemberContext ? 'Team Member Profile' : 'Appointments' },
      { icon: Users, text: isJobContext ? `👥 Are the current linked appointments, assignees, timing, and workload realistic for ${identifier}?` : isAppointmentContext ? `👥 Is this appointment assigned and timed realistically for ${identifier}?` : isProjectContext ? `👥 Which jobs inside ${identifier} are missing realistic linked appointment or linked appointment group capacity?` : isTeamMemberContext ? `👥 Is ${identifier} carrying the right workload, vehicle, and compliance context right now?` : `👥 Are the current assignees, timing, and workload realistic for ${identifier}?`, category: 'Capacity' },
      { icon: Clock, text: isJobContext ? `⏱️ What appointment timing, travel, or linked-group sequencing risks should I address first on ${identifier}?` : isAppointmentContext ? `⏱️ What timing, travel, or follow-up risks should I address first on this appointment for ${identifier}?` : isProjectContext ? `⏱️ Which jobs inside ${identifier} are drifting because they still need linked appointments or linked appointment groups planned?` : isTeamMemberContext ? `⏱️ Which schedule, vehicle, or SmartTask risks should I address first for ${identifier}?` : `⏱️ What timing, travel, or schedule risks should I address first on ${identifier}?`, category: 'Risk Review' },
      { icon: MessageSquare, text: isProjectContext ? `💬 Draft a clear customer update about the next jobs, linked appointments, or linked appointment groups for ${identifier}.` : `💬 Draft a clear update for the customer or technician about ${identifier}.`, category: 'Communication' },
      { icon: ClipboardCheck, text: isJobContext ? `📋 Turn ${identifier} into the best appointment execution plan.` : isAppointmentContext ? `📋 Turn this appointment for ${identifier} into the best next-step execution plan.` : isProjectContext ? `📋 Turn ${identifier} into the best job rollout plan.` : isTeamMemberContext ? `📋 Turn ${identifier} into a practical people-operations action plan.` : `📋 Turn ${identifier} into the best next-step execution plan.`, category: 'Execution Plan' },
      { icon: AlertTriangle, text: `🚨 What is most likely to delay or derail ${identifier}?`, category: 'Delay Risks' },
    ],
  };
}

function buildCvAssistantProfile(contextCv: CvAssistantContext): AssistantProfile {
  const name = contextCv.candidateName?.trim() || 'this candidate';
  const hasCv = Boolean(contextCv.currentCv && Array.isArray(contextCv.currentCv.sections));

  return {
    title: 'AI CV Assistant',
    subtitle: [name, hasCv ? 'CV editing' : 'CV builder'].join(' • '),
    welcomeTitle: `CV Assistant — ${name}`,
    welcomeMessage: hasCv
      ? `I can see ${name}'s CV — ${(contextCv.currentCv?.sections as unknown[]).length} sections. Ask me to fix grammar, tighten wording, rewrite the summary, reorder sections, or reformat entries — I'll return the updated CV for you to review and apply.`
      : `No imported CV yet for ${name}. Describe the candidate's background or paste rough notes and I'll structure a CV you can apply to their profile.`,
    inputPlaceholder: hasCv
      ? 'Describe a CV change — e.g. "fix grammar", "rewrite the summary", "shorten the experience section"...'
      : 'Describe the candidate\'s experience, or paste rough notes to structure...',
    quickActions: [
      { id: 'cv_grammar', label: 'Fix Grammar', icon: CheckCircle, prompt: 'Fix grammar, spelling, punctuation and formatting throughout this CV. Keep all facts exactly as they are.' },
      { id: 'cv_tighten', label: 'Tighten Wording', icon: FileText, prompt: 'Tighten the wording across this CV — remove filler, use active verbs, keep every fact intact.' },
      { id: 'cv_summary', label: 'Rewrite Summary', icon: Sparkles, prompt: `Rewrite the summary/profile section for ${name} to be concise and recruiter-friendly, faithful to the experience on the CV.` },
      { id: 'cv_review', label: 'Review Gaps', icon: AlertTriangle, prompt: 'Review this CV for gaps, missing sections, vague claims, or inconsistencies — tell me what to fix, but do not change the CV yet.' },
    ],
    examplePrompts: [
      { icon: CheckCircle, text: '✍️ Fix grammar and typos throughout the CV', category: 'Polish' },
      { icon: Sparkles, text: '💼 Rewrite the professional summary to be more impactful', category: 'Content' },
      { icon: Layers, text: '🗂️ Reorder sections so experience comes before education', category: 'Structure' },
      { icon: AlertTriangle, text: '🔍 What is this CV missing for a strong application?', category: 'Review' },
    ],
  };
}

function getAssistantUserName(user: ReturnType<typeof useAuth>['user']): string | undefined {
  if (!user) {
    return undefined;
  }

  const fullName = [user.first_name, user.last_name]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .trim();

  return fullName || user.first_name || user.email || undefined;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function JobInsightCard({ insight }: { insight: JobInsight }) {
  const [expanded, setExpanded] = useState(false);
  const { openWorkItem, openWorkItemByNumber, getPermalink } = useWorkItemOpener();
  const riskFactors = Array.isArray(insight.riskFactors) ? insight.riskFactors : [];
  const recommendations = Array.isArray(insight.recommendations) ? insight.recommendations : [];
  const healthScore = typeof insight.healthScore === 'number' ? insight.healthScore : 0;
  const healthTrend = insight.healthTrend || 'stable';
  const slaStatus = insight.slaStatus || 'at_risk';
  const workItemIdentifier = insight.jobNumber || insight.jobId || '';
  const displayNumber = insight.jobNumber || insight.jobId || '';
  const displayTitle = insight.title || 'Untitled work item';
  const canOpenWorkItem = Boolean(workItemIdentifier);
  const linkHref = insight.permalinkUrl || (canOpenWorkItem ? getPermalink(workItemIdentifier) : '');
  const InsightIcon = insight.itemType === 'matter' || insight.itemType === 'project'
    ? Scale
    : insight.itemType === 'contract'
      ? FileText
      : insight.itemType === 'court_date' || insight.itemType === 'appointment'
        ? Calendar
        : Wrench;

  const healthColor = healthScore >= 70 
    ? 'text-green-600 bg-green-50 dark:bg-green-900/30' 
    : healthScore >= 40 
      ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/30'
      : 'text-red-600 bg-red-50 dark:bg-red-900/30';

  const slaColor = slaStatus === 'on_track'
    ? 'text-green-600 bg-green-100'
    : slaStatus === 'at_risk'
      ? 'text-amber-600 bg-amber-100'
      : 'text-red-600 bg-red-100';

  const trendIcon = healthTrend === 'improving' 
    ? <TrendingUp className="w-4 h-4 text-green-500" />
    : healthTrend === 'declining'
      ? <TrendingDown className="w-4 h-4 text-red-500" />
      : <ArrowRight className="w-4 h-4 text-gray-400" />;

  const handleOpenWorkItem = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();

    if (!canOpenWorkItem) {
      event.preventDefault();
      return;
    }

    if (insight.permalinkUrl) {
      return;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();

    if (insight.jobNumber) {
      openWorkItemByNumber(insight.jobNumber);
      return;
    }

    if (insight.jobId) {
      openWorkItem(insight.jobId);
    }
  }, [canOpenWorkItem, insight.jobId, insight.jobNumber, insight.permalinkUrl, openWorkItem, openWorkItemByNumber]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <InsightIcon className="w-4 h-4 text-blue-600" />
              {canOpenWorkItem ? (
                <a
                  href={linkHref}
                  onClick={handleOpenWorkItem}
                  className="group flex min-w-0 items-center gap-2 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                  title={`Open ${displayTitle}`}
                >
                  {displayNumber ? (
                    <span className="text-xs font-mono text-blue-600 dark:text-blue-400 group-hover:underline">
                      {displayNumber}
                    </span>
                  ) : null}
                  <span className="truncate font-medium text-sm text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    {displayTitle}
                  </span>
                </a>
              ) : (
                <>
                  {displayNumber ? <span className="text-xs text-gray-500 font-mono">{displayNumber}</span> : null}
                  <h4 className="font-medium text-gray-900 dark:text-white text-sm">{displayTitle}</h4>
                </>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs mt-2">
              <span className={`px-2 py-0.5 rounded-full font-medium ${healthColor}`}>
                Health: {healthScore}%
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                {trendIcon}
                {healthTrend}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${slaColor}`}>
                SLA: {slaStatus.replace('_', ' ')}
              </span>
            </div>
          </div>
          <button className="p-1">
            {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-3 space-y-3">
          {riskFactors.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-red-600 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Risk Factors
              </h5>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                {riskFactors.map((risk, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-red-400">•</span> {risk}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {recommendations.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-green-600 mb-1 flex items-center gap-1">
                <Lightbulb className="w-3 h-3" /> Recommendations
              </h5>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                {recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-green-400">•</span> {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insight.timeRemaining && (
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                  <Timer className="w-3 h-3" /> Time Remaining
                </span>
                <span className="text-sm font-bold text-blue-700 dark:text-blue-300">{insight.timeRemaining}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResourceCard({ resource }: { resource: ResourceAnalysis }) {
  const skills = Array.isArray(resource.skills) ? resource.skills : [];
  const utilizationColor = resource.utilization >= 90 
    ? 'text-red-600' 
    : resource.utilization >= 70 
      ? 'text-amber-600'
      : 'text-green-600';

  const availabilityBadge = {
    available: 'bg-green-100 text-green-700',
    busy: 'bg-amber-100 text-amber-700',
    overloaded: 'bg-red-100 text-red-700'
  }[resource.availability];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-blue-600" />
          <h4 className="font-medium text-gray-900 dark:text-white text-sm">{resource.technician}</h4>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${availabilityBadge}`}>
          {resource.availability}
        </span>
      </div>
      
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-500">Utilization</span>
          <span className={`font-bold ${utilizationColor}`}>{resource.utilization}%</span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full ${
              resource.utilization >= 90 ? 'bg-red-500' : 
              resource.utilization >= 70 ? 'bg-amber-500' : 'bg-green-500'
            }`}
            style={{ width: `${resource.utilization}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="text-gray-500">Jobs Today</span>
          <p className="font-semibold text-gray-900 dark:text-white">{resource.jobsToday}</p>
        </div>
        <div>
          <span className="text-gray-500">Avg Duration</span>
          <p className="font-semibold text-gray-900 dark:text-white">{resource.avgJobDuration}h</p>
        </div>
      </div>

      {skills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {skills.slice(0, 3).map((skill, i) => (
            <span key={i} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-400">
              {skill}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function KPISummaryCard({ kpis }: { kpis: OperationsKpis }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-indigo-600" />
        <h4 className="font-medium text-gray-900 dark:text-white text-sm">Operations KPIs</h4>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">{kpis.completionRate}%</p>
          <p className="text-xs text-gray-500">Completion Rate</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-600">{kpis.slaCompliance}%</p>
          <p className="text-xs text-gray-500">SLA Compliance</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-600">{kpis.avgResponseTime}</p>
          <p className="text-xs text-gray-500">Avg Response</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-600">{kpis.activeJobs}</p>
          <p className="text-xs text-gray-500">Active Jobs</p>
        </div>
      </div>
    </div>
  );
}

function ScheduleSuggestionCard({ suggestion, onApply }: { suggestion: ScheduleSuggestion; onApply: () => void }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-3 border-b border-gray-100 dark:border-gray-700 bg-purple-50 dark:bg-purple-900/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-purple-600" />
            <span className="font-medium text-sm text-gray-900 dark:text-white">Appointment Plan Suggestion</span>
          </div>
          <span className="text-xs text-purple-600 font-medium">
            {suggestion.confidence}% confidence
          </span>
        </div>
      </div>
      <div className="p-3 space-y-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">{suggestion.reasoning}</p>
        
        {suggestion.assignments?.map((assign: ScheduleSuggestionAssignment, i: number) => (
          <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-gray-750 rounded-lg p-2">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{assign.jobTitle}</p>
              <p className="text-xs text-gray-500">→ {assign.technician} @ {assign.time}</p>
            </div>
          </div>
        ))}

        <button
          onClick={onApply}
          className="w-full mt-2 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
        >
          <Play className="w-3 h-3" />
          Apply Appointment Plan
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// FORM CONFIG PREVIEW CARD - AI-generated form preview
// ============================================================================

function FormConfigPreviewCard({ config, onSave, saving }: { 
  config: FormPreviewConfig; 
  onSave: () => void; 
  saving?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const sections = config?.form_schema?.sections || [];
  const totalFields = sections.reduce((sum: number, s: FormPreviewSection) => sum + (s.fields?.length || 0), 0);

  const getFieldTypeIcon = (type: string) => {
    switch (type) {
      case 'text': case 'textarea': case 'rich_text': return <Type className="w-3 h-3" />;
      case 'number': case 'currency': case 'percentage': return <Hash className="w-3 h-3" />;
      case 'select': case 'multi_select': case 'radio': return <List className="w-3 h-3" />;
      case 'checkbox': case 'toggle': return <ToggleLeft className="w-3 h-3" />;
      case 'date': case 'datetime': case 'time': return <Calendar className="w-3 h-3" />;
      case 'file_upload': case 'image_upload': case 'signature': return <FileText className="w-3 h-3" />;
      default: return <ClipboardCheck className="w-3 h-3" />;
    }
  };

  const categoryColors: Record<string, string> = {
    checklist: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
    inspection: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    safety: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    maintenance: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    report: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    compliance: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  };

  const categoryBadge = (config?.category && categoryColors[config.category])
    || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-gray-700" 
        style={{ background: `linear-gradient(135deg, ${config?.color || '#14b8a6'}15, ${config?.color || '#14b8a6'}05)` }}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl shadow-sm" 
              style={{ backgroundColor: `${config?.color || '#14b8a6'}20`, color: config?.color || '#14b8a6' }}>
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                  {config?.title || 'Untitled Form'}
                </h4>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide ${categoryBadge}`}>
                  {config?.category || 'form'}
                </span>
              </div>
              {config?.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{config.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3 ml-12">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Layers className="w-3.5 h-3.5" />
            <span><strong className="text-gray-700 dark:text-gray-300">{sections.length}</strong> sections</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <ClipboardCheck className="w-3.5 h-3.5" />
            <span><strong className="text-gray-700 dark:text-gray-300">{totalFields}</strong> fields</span>
          </div>
          {config?.applies_to_item_type && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Target className="w-3.5 h-3.5" />
              <span>for <strong className="text-gray-700 dark:text-gray-300">{config.applies_to_item_type}</strong></span>
            </div>
          )}
        </div>
      </div>

      {/* Sections preview */}
      <div className="p-4 space-y-2">
        {sections.map((section: FormPreviewSection, si: number) => {
          const sectionFields = section.fields || [];

          return (
          <div key={si} className="bg-gray-50 dark:bg-gray-750 rounded-lg p-3">
            <div 
              className="flex items-center justify-between cursor-pointer" 
              onClick={() => setExpanded(expanded ? false : true)}
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-[10px] font-bold text-gray-500">
                  {si + 1}
                </div>
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{section.title}</span>
                <span className="text-[10px] text-gray-400">{sectionFields.length} fields</span>
              </div>
              {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
            </div>

            {expanded && sectionFields.length > 0 && (
              <div className="mt-2 ml-7 space-y-1">
                {sectionFields.map((field: FormPreviewField, fi: number) => (
                  <div key={fi} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 py-0.5">
                    <span className="text-gray-400">{getFieldTypeIcon(field.type || 'unknown')}</span>
                    <span>{field.label}</span>
                    {field.is_required && (
                      <span className="text-red-400 text-[10px]">required</span>
                    )}
                    <span className="text-gray-300 dark:text-gray-600 ml-auto text-[10px]">{field.type}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );})}
      </div>

      {/* Actions */}
      <div className="p-4 pt-0 flex gap-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 py-2 px-3 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors flex items-center justify-center gap-1.5"
        >
          <Eye className="w-3.5 h-3.5" />
          {expanded ? 'Collapse' : 'Preview Fields'}
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex-1 py-2 px-3 text-xs font-medium rounded-lg text-white transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
          style={{ backgroundColor: config?.color || '#14b8a6' }}
        >
          {saving ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-3.5 h-3.5" />
              Save Form
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function OperationsActionsCard({ actions, onApply }: { 
  actions: AISuggestedAction[]; 
  onApply: (action: AISuggestedAction) => void;
}) {
  const getActionIcon = (type: string) => {
    switch (type) {
      case 'create_job': return <Plus className="w-4 h-4" />;
      case 'assign_technician': return <Users className="w-4 h-4" />;
      case 'reschedule': return <Calendar className="w-4 h-4" />;
      case 'update_status': return <CheckCircle className="w-4 h-4" />;
      case 'send_notification': return <MessageSquare className="w-4 h-4" />;
      case 'optimize_route': return <Route className="w-4 h-4" />;
      case 'escalate': return <AlertTriangle className="w-4 h-4" />;
      case 'create_form': return <ClipboardCheck className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const getActionColor = (type: string) => {
    switch (type) {
      case 'create_job': return 'bg-green-100 dark:bg-green-900/30 text-green-600';
      case 'assign_technician': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600';
      case 'reschedule': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600';
      case 'escalate': return 'bg-red-100 dark:bg-red-900/30 text-red-600';
      case 'create_form': return 'bg-teal-100 dark:bg-teal-900/30 text-teal-600';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-3 border-b border-gray-100 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/30">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-600" />
          <span className="font-medium text-sm text-gray-900 dark:text-white">Recommended Actions</span>
        </div>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {actions.map((action) => (
          <div key={action.id} className="p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${getActionColor(action.type)}`}>
                {getActionIcon(action.type)}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{action.label}</p>
              </div>
            </div>
            <button
              onClick={() => onApply(action)}
              disabled={action.applied}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                action.applied 
                  ? 'bg-green-100 text-green-600 cursor-default'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {action.applied ? 'Done' : 'Apply'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

// ============================================================================
// CV PREVIEW CARD - AI-edited ParsedCv preview (recruitment context)
// ============================================================================

interface CvPreviewSectionLine {
  text?: string;
  kind?: string;
}

interface CvPreviewSection {
  key?: string;
  title?: string;
  lines?: CvPreviewSectionLine[];
}

interface CvPreviewConfig {
  name?: string;
  headline?: string;
  sections?: CvPreviewSection[];
}

function CvPreviewCard({ cv, truncated, onApply }: {
  cv: CvPreviewConfig;
  truncated?: boolean;
  onApply?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const sections = Array.isArray(cv?.sections) ? cv.sections : [];
  const totalLines = sections.reduce((sum, s) => sum + (s.lines?.length || 0), 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      <div className="p-3 border-b border-gray-100 dark:border-gray-700 bg-teal-50 dark:bg-teal-900/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-teal-600 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                {cv?.name || 'Updated CV'}
              </p>
              {cv?.headline && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{cv.headline}</p>
              )}
            </div>
          </div>
          <span className="text-[10px] text-teal-700 dark:text-teal-300 font-medium shrink-0 ml-2">
            {sections.length} sections · {totalLines} lines
          </span>
        </div>
      </div>

      <div className="p-3">
        {truncated && (
          <p className="mb-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
            This preview may be incomplete — the response was cut short. Missing
            sections will be restored from the current CV on apply, but review
            carefully before applying.
          </p>
        )}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? 'Hide section preview' : 'Preview sections'}
        </button>
        {expanded && (
          <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto">
            {sections.map((s, i) => (
              <div key={i} className="bg-gray-50 dark:bg-gray-750 rounded-lg px-2.5 py-1.5">
                <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{s.title || s.key || `Section ${i + 1}`}</p>
                <p className="text-[10px] text-gray-400">{s.lines?.length || 0} lines</p>
              </div>
            ))}
          </div>
        )}
        {onApply && (
          <button
            onClick={onApply}
            className="w-full mt-3 py-2 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Apply to CV
          </button>
        )}
      </div>
    </div>
  );
}

export default function AIOperationsAssistant({ 
  permissions,
  contextJobId, 
  contextProjectId,
  activeSolutionKey,
  activeWorkflowTemplates,
  initialPrompt,
  contextWorkItem,
  contextCv,
  onApplyCv,
  autoSendRequest,
  embedded = false,
  floatingPanel = false,
  onClose,
  showProviderToggle,
}: AIOperationsAssistantProps) {
  void permissions;

  const { user } = useAuth();
  const { moduleColor } = useModuleColor('total-operations');
  const operationsContext = useOptionalOperations();
  const contextSolutionKey = operationsContext?.activeSolutionKey || null;
  const activeSolutionTeam = operationsContext?.activeSolutionTeam || null;
  const userSolutionTeams = useMemo(
    () => operationsContext?.userSolutionTeams || [],
    [operationsContext?.userSolutionTeams]
  );
  
  const [savingFormId, setSavingFormId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [chatInitialPrompt, setChatInitialPrompt] = useState(initialPrompt || '');

  const effectiveSolutionKey = activeSolutionKey || contextSolutionKey || null;

  const resolvedSolutionTeam = useMemo(() => {
    if (effectiveSolutionKey && activeSolutionTeam?.solutionKey === effectiveSolutionKey) {
      return activeSolutionTeam;
    }

    if (effectiveSolutionKey) {
      return userSolutionTeams.find((team) => team.solutionKey === effectiveSolutionKey) || activeSolutionTeam || null;
    }

    return activeSolutionTeam || null;
  }, [effectiveSolutionKey, activeSolutionTeam, userSolutionTeams]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvedWorkflowTemplates = useMemo(
    () => normalizeWorkflowTemplateDescriptors(activeWorkflowTemplates),
    [activeWorkflowTemplates]
  );

  const solutionItemTypes = useMemo(() => {
    const teamItemTypes = normalizeSolutionItemTypes(resolvedSolutionTeam?.itemTypes);

    if (teamItemTypes.length > 0) {
      return teamItemTypes;
    }

    return Array.from(new Set(resolvedWorkflowTemplates.flatMap((template) => template.itemTypes)));
  }, [resolvedSolutionTeam?.itemTypes, resolvedWorkflowTemplates]);

  const effectiveSolutionName = useMemo(() => {
    return resolvedSolutionTeam?.solutionName || humanizeSolutionKey(effectiveSolutionKey);
  }, [resolvedSolutionTeam?.solutionName, effectiveSolutionKey]);

  const assistantProfile = useMemo(() => buildAssistantProfile({
    solutionKey: effectiveSolutionKey,
    solutionName: effectiveSolutionName,
    workflowTemplates: resolvedWorkflowTemplates,
  }), [effectiveSolutionKey, effectiveSolutionName, resolvedWorkflowTemplates]);

  const contextualAssistantProfile = useMemo(() => {
    if (contextCv) {
      return buildCvAssistantProfile(contextCv);
    }
    return contextWorkItem ? buildWorkItemAssistantProfile(contextWorkItem) : null;
  }, [contextCv, contextWorkItem]);

  const resolvedAssistantProfile = contextualAssistantProfile || assistantProfile;
  // Candidate CV editing rides the dedicated 'recruitment' module context —
  // the chat route returns <cv_config> payloads as cv_preview attachments.
  const effectiveModuleContext = contextCv ? 'recruitment' : 'operations';
  const resolvedUserName = useMemo(() => getAssistantUserName(user), [user]);

  const normalizedAssigneeNames = useMemo(() => {
    if (!Array.isArray(contextWorkItem?.assigneeNames)) {
      return [] as string[];
    }

    return contextWorkItem.assigneeNames
      .map((name) => typeof name === 'string' ? name.trim() : '')
      .filter(Boolean);
  }, [contextWorkItem?.assigneeNames]);

  const assistantContextPayload = useMemo(() => ({
    jobId: contextJobId,
    projectId: contextProjectId,
    solutionKey: effectiveSolutionKey,
    solutionName: effectiveSolutionName,
    solutionItemTypes,
    scheduleReferenceDate: contextWorkItem?.scheduledStart || contextWorkItem?.dueDate || undefined,
    activeWorkflowTemplates: resolvedWorkflowTemplates,
    ...(contextWorkItem ? {
      workItemId: contextWorkItem.workItemId,
      activeTab: contextWorkItem.activeTab,
      workItem: {
        id: contextWorkItem.workItemId,
        item_number: contextWorkItem.workItemNumber,
        title: contextWorkItem.title,
        item_type: contextWorkItem.itemType,
        item_subtype: contextWorkItem.itemSubtype,
        status: contextWorkItem.status,
        priority: contextWorkItem.priority,
        scheduled_start: contextWorkItem.scheduledStart,
        scheduled_end: contextWorkItem.scheduledEnd,
        due_date: contextWorkItem.dueDate,
        estimated_hours: contextWorkItem.estimatedHours,
        progress_percentage: contextWorkItem.progressPercentage,
        contact_name: contextWorkItem.contactName,
        location_name: contextWorkItem.locationName,
        assignee_names: normalizedAssigneeNames,
      }
    } : {}),
    ...(contextCv ? {
      candidate: {
        id: contextCv.candidateId,
        name: contextCv.candidateName,
        current_title: contextCv.currentTitle,
        current_company: contextCv.currentCompany,
        email: contextCv.email,
        phone: contextCv.phone,
      },
      // The live ParsedCv draft — the chat route embeds it in the system
      // prompt and returns an updated copy as a cv_preview attachment.
      // raw_text is stripped: it's never edited and inflates every request.
      currentCv: contextCv.currentCv
        ? { ...contextCv.currentCv, raw_text: undefined }
        : null,
    } : {}),
  }), [
    contextJobId,
    contextProjectId,
    effectiveSolutionKey,
    effectiveSolutionName,
    solutionItemTypes,
    resolvedWorkflowTemplates,
    contextWorkItem,
    contextCv,
    normalizedAssigneeNames,
  ]);

  const sessionScopeKey = useMemo(() => [
    embedded ? 'embedded' : 'page',
    effectiveSolutionKey || 'operations',
    contextJobId || '',
    contextProjectId || '',
    contextWorkItem?.workItemId || '',
    contextCv?.candidateId || '',
  ].join('::'), [embedded, effectiveSolutionKey, contextJobId, contextProjectId, contextWorkItem?.workItemId, contextCv?.candidateId]);

  // Per-candidate chat threads — pin a session id so the embedded CV
  // assistant only ever shows THIS candidate's conversation. Without it the
  // hook falls back to load_latest, which is module-wide by design (the
  // ai-chat-sessions API intentionally ignores viewId) and bleeds unrelated
  // operations/forms history into the panel. Unsaved candidates get a
  // per-designer draft id; once saved, the candidate id takes over.
  const cvDraftSessionId = useMemo(
    () => `cv-draft-${crypto.randomUUID()}`,
    [], // eslint-disable-line react-hooks/exhaustive-deps -- one id per mount
  );
  const cvResumeSessionId = contextCv
    ? (contextCv.candidateId ? `cv-candidate-${contextCv.candidateId}` : cvDraftSessionId)
    : undefined;
  // CV editing needs Gemini's context window — total-vision can't carry a
  // full ParsedCv plus enrichment source text.
  const cvProviderOverride = contextCv ? { provider: 'gemini' } : undefined;

  const effectiveShowProviderToggle = showProviderToggle ?? !embedded;

  const {
    messages,
    isGenerating,
    credits,
    sendMessage,
    newChat: hookNewChat,
    feedback,
    applyAction: hookApplyAction,
    loadSession,
  } = useAIAssistant({
    // CV sessions get their own moduleKey so they never enter the
    // total-operations "latest session" pool (load_latest ignores viewId by
    // design — moduleKey is the only real boundary) and vice-versa.
    moduleKey: contextCv ? 'total-operations-cv' : 'total-operations',
    moduleContext: effectiveModuleContext,
    contextPayload: assistantContextPayload,
    sessionScopeKey,
    resumeSessionId: cvResumeSessionId,
    // CV editing pins Gemini — total-vision's 32k context can't carry a full
    // ParsedCv plus pasted/fetched profile source text. The hook default
    // covers programmatic sends; composer sends go through providerOverride.
    provider: contextCv ? 'gemini' : undefined,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Programmatic sends (e.g. "merge this pasted profile text into the CV")
  // — keyed so repeated requests with identical text still fire. Deferred
  // while a generation is in-flight: sendMessage silently drops calls then,
  // so the effect re-runs once isGenerating flips false.
  const lastAutoSendKeyRef = useRef<string | number | null>(null);
  useEffect(() => {
    if (!autoSendRequest || !autoSendRequest.text.trim()) return;
    if (autoSendRequest.key === lastAutoSendKeyRef.current) return;
    if (isGenerating) return;
    lastAutoSendKeyRef.current = autoSendRequest.key;
    void sendMessage(autoSendRequest.text);
  }, [autoSendRequest, sendMessage, isGenerating]);

  useEffect(() => {
    setChatInitialPrompt(initialPrompt || '');
  }, [initialPrompt]);

  // Scroll to top when component mounts - traverse all parent containers
  useEffect(() => {
    if (embedded) {
      return;
    }

    window.scrollTo({ top: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    
    const scrollParents = () => {
      let parent = containerRef.current?.parentElement;
      while (parent) {
        parent.scrollTop = 0;
        parent = parent.parentElement;
      }
    };
    scrollParents();
    const timer = setTimeout(scrollParents, 150);
    return () => clearTimeout(timer);
  }, [embedded]);

  const handleQuickAction = useCallback((actionId: string) => {
    const action = resolvedAssistantProfile.quickActions.find((quickAction) => quickAction.id === actionId);
    if (!action?.prompt) {
      return;
    }

    setChatInitialPrompt(action.prompt);
  }, [resolvedAssistantProfile.quickActions]);

  // Save AI-generated form directly from the preview card
  const handleSaveFormConfig = useCallback(async (formConfig: FormPreviewConfig, attachmentId?: string) => {
    const saveId = attachmentId || `form-${Date.now()}`;
    setSavingFormId(saveId);
    try {
      // Use the existing applyAction flow
      await hookApplyAction({
        id: `create-form-${Date.now()}`,
        label: `Save Form: ${formConfig?.title || 'Untitled'}`,
        type: 'create_form',
        data: formConfig
      });
    } finally {
      setSavingFormId(null);
    }
  }, [hookApplyAction]);

  // Custom attachment renderer
  const renderAttachment = useCallback((attachment: AIAttachment) => {
    switch (attachment.type) {
      case 'job_insights':
      case 'work_item_insights': {
        const insights = Array.isArray(attachment.data) ? attachment.data : [];
        return insights.map((insight: JobInsight, i: number) => (
          <JobInsightCard key={`${insight.jobId || insight.jobNumber || 'insight'}-${i}`} insight={insight} />
        ));
      }
      case 'resource_analysis':
        return (Array.isArray(attachment.data) ? attachment.data : []).map((resource: ResourceAnalysis, i: number) => (
          <ResourceCard key={i} resource={resource} />
        ));
      case 'schedule_suggestion':
        return (
          <ScheduleSuggestionCard 
            suggestion={attachment.data} 
            onApply={() => hookApplyAction({
              id: `schedule-${Date.now()}`,
              label: 'Apply Schedule',
              type: 'reschedule',
              data: attachment.data
            })} 
          />
        );
      case 'kpi_summary':
        return <KPISummaryCard kpis={attachment.data} />;
      case 'form_config_preview':
        return (
          <FormConfigPreviewCard
            config={attachment.data}
            onSave={() => handleSaveFormConfig(attachment.data, attachment.title)}
            saving={savingFormId === attachment.title}
          />
        );
      case 'cv_preview':
        return (
          <CvPreviewCard
            cv={attachment.data}
            truncated={attachment.truncated}
            onApply={onApplyCv ? () => onApplyCv(attachment.data, { truncated: attachment.truncated }) : undefined}
          />
        );
      default:
        return null;
    }
  }, [hookApplyAction, handleSaveFormConfig, savingFormId, onApplyCv]);

  // Custom actions renderer
  const renderActions = useCallback((actions: AISuggestedAction[], onApply: (action: AISuggestedAction) => void) => {
    return <OperationsActionsCard actions={actions} onApply={onApply} />;
  }, []);

  if (floatingPanel) {
    return (
      <AIAssistantChat
        title={resolvedAssistantProfile.title}
        subtitle={resolvedAssistantProfile.subtitle}
        moduleContext={effectiveModuleContext}
        moduleKey="total-operations"
        welcomeTitle={resolvedAssistantProfile.welcomeTitle}
        welcomeMessage={resolvedAssistantProfile.welcomeMessage}
        inputPlaceholder={resolvedAssistantProfile.inputPlaceholder}
        userName={resolvedUserName}

        messages={messages}
        isGenerating={isGenerating}
        creditsRemaining={credits?.remaining ?? 0}
        creditsTotal={credits?.total ?? 0}
        creditsResetDate={credits?.resetDate ?? undefined}
        showCredits={false}

        quickActions={resolvedAssistantProfile.quickActions}
        examplePrompts={resolvedAssistantProfile.examplePrompts}
        showQuickActions={false}
        showQuickActionsInHeader={false}

        onSendMessage={sendMessage}
        onQuickAction={handleQuickAction}
        onNewChat={() => { hookNewChat(); setChatInitialPrompt(''); }}
        onFeedback={feedback}
        onApplyAction={hookApplyAction}
        onClose={onClose}

        showHistory={false}

        renderAttachment={renderAttachment}
        renderActions={renderActions}

        useUnifiedTheming={true}
        primaryColor={moduleColor}
        isMinimized={false}
        minimizable={false}
        closable={Boolean(onClose)}

        useBrandedSpinner={true}
        spinnerSize="sm"
        initialPrompt={chatInitialPrompt}
        inputRows={2}
        inputMinHeightClassName="min-h-[44px]"
        inputMaxHeightClassName="max-h-[160px]"
        hideHeader={false}
        showProviderToggle={effectiveShowProviderToggle}
        providerOverride={cvProviderOverride}
        disableInitialScrollReset={true}

        floatingPanel={true}
      />
    );
  }

  if (embedded) {
    return (
      <div ref={containerRef} className="h-full flex flex-col">
        <AIAssistantChat
          title={resolvedAssistantProfile.title}
          subtitle={resolvedAssistantProfile.subtitle}
          moduleContext={effectiveModuleContext}
          moduleKey="total-operations"
          welcomeTitle={resolvedAssistantProfile.welcomeTitle}
          welcomeMessage={resolvedAssistantProfile.welcomeMessage}
          inputPlaceholder={resolvedAssistantProfile.inputPlaceholder}
          userName={resolvedUserName}

          messages={messages}
          isGenerating={isGenerating}
          creditsRemaining={credits?.remaining ?? 0}
          creditsTotal={credits?.total ?? 0}
          creditsResetDate={credits?.resetDate ?? undefined}
          showCredits={false}

          quickActions={resolvedAssistantProfile.quickActions}
          examplePrompts={resolvedAssistantProfile.examplePrompts}
          showQuickActions={false}
          showQuickActionsInHeader={false}

          onSendMessage={sendMessage}
          onQuickAction={handleQuickAction}
          onNewChat={() => { hookNewChat(); setChatInitialPrompt(''); }}
          onFeedback={feedback}
          onApplyAction={hookApplyAction}
          onClose={onClose}

          showHistory={false}

          renderAttachment={renderAttachment}
          renderActions={renderActions}

          useUnifiedTheming={true}
          primaryColor={moduleColor}
          isMinimized={false}
          minimizable={false}
          closable={Boolean(onClose)}

          useBrandedSpinner={true}
          spinnerSize="sm"
          initialPrompt={chatInitialPrompt}
          inputRows={3}
          inputMinHeightClassName="min-h-[88px] sm:min-h-[104px]"
          inputMaxHeightClassName="max-h-[240px]"
          hideHeader={false}
          showProviderToggle={effectiveShowProviderToggle}
          providerOverride={cvProviderOverride}
          disableInitialScrollReset={true}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      <div className="flex-1 min-h-0">
      <AIAssistantChat
        title={resolvedAssistantProfile.title}
        subtitle={resolvedAssistantProfile.subtitle}
        moduleContext={effectiveModuleContext}
        moduleKey="total-operations"
        welcomeTitle={resolvedAssistantProfile.welcomeTitle}
        welcomeMessage={resolvedAssistantProfile.welcomeMessage}
        inputPlaceholder={resolvedAssistantProfile.inputPlaceholder}
        userName={resolvedUserName}
        
        messages={messages}
        isGenerating={isGenerating}
        creditsRemaining={credits?.remaining ?? 0}
        creditsTotal={credits?.total ?? 0}
        creditsResetDate={credits?.resetDate ?? undefined}
        showCredits={true}
        
        quickActions={resolvedAssistantProfile.quickActions}
        examplePrompts={resolvedAssistantProfile.examplePrompts}
        showQuickActions={false}
        showQuickActionsInHeader={false}
        
        onSendMessage={sendMessage}
        onQuickAction={handleQuickAction}
        onNewChat={() => { hookNewChat(); setChatInitialPrompt(''); }}
        onFeedback={feedback}
        onApplyAction={hookApplyAction}
        onClose={onClose}
        
        showHistory={true}
        onLoadSession={loadSession}
        externalHistoryOpen={historyOpen}
        onHistoryOpenChange={setHistoryOpen}
        
        renderAttachment={renderAttachment}
        renderActions={renderActions}
        
        // Uses unified theming from clientBranding primary color
        useUnifiedTheming={true}
        primaryColor={moduleColor}
        headerStorageKey="ta_ai_assistant_header"
        isMinimized={false}
        minimizable={false}
        closable={Boolean(onClose)}
        
        useBrandedSpinner={true}
        spinnerSize="sm"
        initialPrompt={chatInitialPrompt}
        showProviderToggle={effectiveShowProviderToggle}
        providerOverride={cvProviderOverride}
        disableInitialScrollReset={false}
      />
      </div>
    </div>
  );
}
